<script setup lang="ts">
import {
  ArrowLeft,
  Briefcase,
  Edit,
  Message,
  Phone,
  Refresh,
  User,
  Document,
} from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  Entity,
  EntityContentDto,
  EntityDefinitionDto,
  FormElementType,
  HousingApplication,
  LinkedWorkflowSummaryDto,
  WorkflowDefinitionDto,
  WorkflowDto,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { ApplicationDrawerTabs } from "~/YYC_Housing/HMB/types/hmbEnums";
import EntityWorkflowsPanel from "../components/EntityWorkflowsPanel.vue";
import WorkflowResponseViewer from "../components/WorkflowResponseViewer.vue";
import FormResponseTemplate from "./FormResponseTemplate.vue";
import HousingApplicationEntityViewer from "../../YYC_Housing/HMB/components/HousingApplicationEntityViewer.vue";

// Props for drawer mode
const props = withDefaults(
  defineProps<{
    entityDefinitionGuid?: string;
    entityGuid?: string;
    isDrawer?: boolean;
  }>(),
  {
    entityDefinitionGuid: "",
    entityGuid: "",
    isDrawer: false,
  },
);

const route = useRoute();
const router = useRouter();
const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();

// Route params (fallback to props for drawer mode)
const entityDefinitionGuid = computed(
  () =>
    props.entityDefinitionGuid ||
    (route.params.entityDefinitionGuid as string) ||
    "",
);
const entityGuid = computed(
  () => props.entityGuid || (route.params.entityGuid as string) || "",
);

// State
const loading = ref(true);
const entity = ref<Entity | null>(null);
const entityContent = ref<EntityContentDto | null>(null);
const currentEntityDefinition = ref<EntityDefinitionDto | null>(null);
const linkedWorkflows = ref<LinkedWorkflowSummaryDto[]>([]);
const workflowDefinitions = ref<WorkflowDefinitionDto[]>([]);

// Drawer state for viewing workflows
const workflowDrawerOpen = ref(false);
const selectedWorkflow = ref<WorkflowDto | null>(null);

// Drawer state for viewing housing application
const housingAppDrawerOpen = ref(false);
const selectedHousingApp = ref<HousingApplication | null>(null);

// Drawer state for editing entity details
const showEntityDetailsForm = ref(false);

// Editing state
const isEditing = ref(false);
const editedName = ref("");

const CONTACT_FIELD_LABELS: Record<string, string> = {
  email: "Email",
  phone: "Phone",
  role: "Role",
  primarycontact: "Primary Contact",
  notes: "Notes",
};

const normalizeFieldKey = (value?: string | null) =>
  (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const parseAnswerValue = (rawValue?: string | null) => {
  if (!rawValue) {
    return "-";
  }

  let value: string | null = null;

  try {
    const parsed = JSON.parse(rawValue);
    value =
      typeof parsed === "object" && parsed !== null
        ? (parsed.value ?? parsed.text ?? JSON.stringify(parsed))
        : String(parsed);
  } catch {
    value = rawValue;
  }

  return value && value !== "null" && value !== "" ? value : "-";
};

const isContactEntityDefinition = computed(() => {
  const definition = currentEntityDefinition.value as Record<
    string,
    unknown
  > | null;

  if (!definition) {
    return false;
  }

  const candidateValues = [
    definition["name"],
    definition["label"],
    definition["title"],
    definition["displayName"],
    definition["entityDefinitionName"],
  ];

  return candidateValues.some(
    (value) =>
      typeof value === "string" && normalizeFieldKey(value).includes("contact"),
  );
});

// Extract fields from form response answers using each answer's metadata
const entityFields = computed(() => {
  const components =
    entityContent.value?.formResponse?.formDefinition?.components ?? [];
  const answers = entityContent.value?.formResponse?.answers ?? [];

  // Map from formElement guid -> answerValue
  const answerMap = new Map(
    answers.map((answer) => [answer.formElementGuid, answer.answerValue]),
  );

  return components
    .filter((component) => component.type !== ("Section" as FormElementType))
    .map((component) => {
      const questionCode = component.questionCode ?? "";
      const formLabel = component.label ?? "";
      const normalizedQuestionCode = normalizeFieldKey(questionCode);
      const normalizedLabel = normalizeFieldKey(formLabel);
      const contactLabel =
        CONTACT_FIELD_LABELS[normalizedQuestionCode] ??
        CONTACT_FIELD_LABELS[normalizedLabel];

      if (isContactEntityDefinition.value && !contactLabel) {
        return null;
      }

      const answerValue = answerMap.get(component.guid);

      return {
        label: contactLabel || formLabel || questionCode || "Field",
        value: parseAnswerValue(answerValue),
      };
    })
    .filter(
      (field): field is { label: string; value: string } => field !== null,
    );
});

// Stats computed from workflows
const _stats = computed(() => {
  const active = linkedWorkflows.value.filter(
    (w) => !w.workflowArchivedAt && w.isWorkflowComplete === false,
  ).length;
  // TODO: Define what "pending" means more clearly
  const pending = linkedWorkflows.value.filter(
    (w) =>
      !w.workflowArchivedAt &&
      w.workflowStatus?.toLowerCase().includes("pending"),
  ).length;
  const completed = linkedWorkflows.value.filter(
    (w) => w.workflowArchivedAt || w.isWorkflowComplete === true,
  ).length;

  // Find most recent workflow date
  let lastContact = "-";
  if (linkedWorkflows.value.length > 0) {
    const sorted = [...linkedWorkflows.value].sort((a, b) => {
      const dateA = a.linkedAt ? new Date(a.linkedAt).getTime() : 0;
      const dateB = b.linkedAt ? new Date(b.linkedAt).getTime() : 0;
      return dateB - dateA;
    });
    if (sorted[0]?.linkedAt) {
      const date = new Date(sorted[0].linkedAt);
      const now = new Date();
      const diffDays = Math.floor(
        (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (diffDays === 0) {
        lastContact = "Today";
      } else if (diffDays === 1) {
        lastContact = "Yesterday";
      } else if (diffDays < 7) {
        lastContact = `${diffDays} days ago`;
      } else {
        lastContact = date.toLocaleDateString();
      }
    }
  }

  return { active, pending, completed, lastContact };
});

// Load entity data
const loadEntity = async () => {
  if (!entityGuid.value) {
    return;
  }

  loading.value = true;
  try {
    const content = await apiClient.entity.getEntity(entityGuid.value);
    entityContent.value = content;
    entity.value = content.entity;
    editedName.value = entity.value?.name ?? "";
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
};

// Load linked workflows
const loadWorkflows = async () => {
  if (!entityGuid.value) {
    return;
  }

  try {
    linkedWorkflows.value =
      await apiClient.workflowInstance.getLinkedWorkflowsWithArchived(
        entityGuid.value,
        true,
      );
  } catch (error) {
    showError(error);
  }
};

// Find entity definition helper
const findEntityDefinition = (
  entities: EntityDefinitionDto[],
  guid: string,
): EntityDefinitionDto | null => {
  for (const entityDef of entities) {
    if (entityDef.guid === guid) {
      return entityDef;
    }
    if (entityDef.childEntityDefinitions?.length) {
      const found = findEntityDefinition(
        entityDef.childEntityDefinitions,
        guid,
      );
      if (found) {
        return found;
      }
    }
  }
  return null;
};

// Load all data
const loadData = async () => {
  await Promise.all([
    myInfoStore.load(),
    orgInfoStore.load(),
    loadEntity(),
    loadWorkflows(),
  ]);

  // Get entity definition and workflow definitions
  currentEntityDefinition.value = findEntityDefinition(
    orgInfoStore.MyOrgInfo?.entityDefinitionDtos ?? [],
    entityDefinitionGuid.value,
  );
  workflowDefinitions.value =
    orgInfoStore.MyOrgInfo?.workflowDefinitionDtos.filter(
      (wd) => wd.entityDefinitionGuid === entityDefinitionGuid.value,
    ) ?? [];
};

// Navigate back to entity list
const goBack = () => {
  // If there's in-app history, use it; otherwise fall back to the parent list route
  if (globalThis.history.state?.back) {
    router.back();
  } else {
    // Build the list path from the current route by dropping the entityGuid segment
    const path = route.path.replace(/\/[^/]+$/, "");
    router.push(path);
  }
};

const cancelEditing = () => {
  editedName.value = entity.value?.name ?? "";
  isEditing.value = false;
};

const saveEntityName = async () => {
  if (!entity.value || !editedName.value.trim()) {
    ElMessage.warning("Name cannot be empty");
    return;
  }

  try {
    const updated = await apiClient.entity.updateEntity(
      myInfoStore.currentSysOrgGuid,
      {
        ...entity.value,
        name: editedName.value.trim(),
      },
    );
    entity.value = updated;
    isEditing.value = false;
    ElMessage.success("Entity updated");
  } catch (error) {
    showError(error);
  }
};

// Create new workflow for this entity
const _createWorkflow = async (workflowDefinitionGuid: string) => {
  if (!entity.value) {
    return;
  }

  try {
    // Use the createWorkflowInstance method with entity guid
    const newWorkflow = await apiClient.workflowInstance.createWorkflowInstance(
      workflowDefinitionGuid,
      entity.value.guid,
      myInfoStore.currentSysOrgGuid,
      "", // workflow name (optional)
    );

    ElMessage.success("Workflow created");

    // Reload workflows and open the new one
    await loadWorkflows();
    openWorkflowDrawer(newWorkflow.guid);
  } catch (error) {
    showError(error);
  }
};

// Convert LinkedWorkflowSummaryDto to WorkflowDto for the viewer
const convertToWorkflowDto = (
  linkedWorkflow: LinkedWorkflowSummaryDto,
): WorkflowDto => {
  return {
    guid: linkedWorkflow.workflowGuid,
    workflowStatus: linkedWorkflow.workflowStatus ?? "",
    workflowName: linkedWorkflow.workflowName ?? "",
    workflowArchivedAt: linkedWorkflow.workflowArchivedAt ?? "",
    workflowDefinitionName: linkedWorkflow.workflowDefinitionName ?? "",
    workflowDefinitionGuid: linkedWorkflow.workflowDefinitionGuid ?? "",
    ownerName: linkedWorkflow.primaryEntityName ?? "",
    ownerGuid: linkedWorkflow.primaryEntityGuid,
    answersToShow: {},
    updatedAt: linkedWorkflow.linkedAt ?? "",
  };
};

// Open workflow detail drawer
const openWorkflowDrawer = async (workflowGuid: string) => {
  // Find the workflow in our linked list to get basic info
  const linkedWf = linkedWorkflows.value.find(
    (w) => w.workflowGuid === workflowGuid,
  );
  if (linkedWf) {
    selectedWorkflow.value = convertToWorkflowDto(linkedWf);
    workflowDrawerOpen.value = true;
  } else {
    // If not found in list, we need to fetch it (e.g., for newly created workflows)
    try {
      const content =
        await apiClient.workflowInstance.getWorkflow(workflowGuid);
      // Create a minimal WorkflowDto from the content
      selectedWorkflow.value = {
        guid: content.workflow.guid,
        workflowStatus: content.workflow.status ?? "",
        workflowName: content.workflow.name ?? "",
        workflowArchivedAt: content.workflow.archivedAt ?? "",
        workflowDefinitionName: content.workflow.workflowDefinition?.name ?? "",
        workflowDefinitionGuid: content.workflow.workflowDefinition?.guid ?? "",
        ownerName: entity.value?.name ?? "",
        ownerGuid: entity.value?.guid,
        answersToShow: {},
        updatedAt: content.workflow.createdAt ?? "",
      };
      workflowDrawerOpen.value = true;
    } catch (error) {
      showError(error);
    }
  }
};

// Handle workflow panel events
const handleWorkflowSelected = async (workflowGuid: string) => {
  // Clear any previously opened housing app drawer
  selectedHousingApp.value = null;
  housingAppDrawerOpen.value = false;

  // Find the linked workflow to get the primary entity GUID
  const linkedWorkflow = linkedWorkflows.value.find(
    (w) => w.workflowGuid === workflowGuid,
  );

  if (linkedWorkflow?.primaryEntityGuid) {
    try {
      // Fetch the full housing application data
      const fullHousingApp =
        await apiClient.housingApplication.getHousingApplicationByEntityGuid(
          linkedWorkflow.primaryEntityGuid,
        );
      selectedHousingApp.value = fullHousingApp;
      housingAppDrawerOpen.value = true;
    } catch (_error) {
      // If fetching the housing application fails, fall back to showing the workflow details
      await openWorkflowDrawer(workflowGuid);
    }
  } else {
    await openWorkflowDrawer(workflowGuid);
  }
};

const handleWorkflowUpdated = () => {
  loadWorkflows();
};

const handleHousingAppDrawerClosed = () => {
  housingAppDrawerOpen.value = false;
  selectedHousingApp.value = null;
};

const handleDrawerClose = () => {
  workflowDrawerOpen.value = false;
  selectedWorkflow.value = null;
  loadWorkflows(); // Refresh list in case something changed
};

// Watch for route changes
watch(
  () => route.params.entityGuid,
  async (newGuid) => {
    if (newGuid) {
      await loadData();
    }
  },
);

// Watch computed entityGuid for drawer mode prop changes
watch(entityGuid, async (newGuid) => {
  if (newGuid && props.isDrawer) {
    await loadData();
  }
});

onMounted(() => {
  loadData();
});
</script>

<template>
  <div class="CRMEntityDetail">
    <!-- Header (hidden in drawer mode) -->
    <div v-if="!props.isDrawer" class="detail-header">
      <div class="header-left">
        <el-button :icon="ArrowLeft" text @click="goBack">
          Back to {{ currentEntityDefinition?.plural ?? "Entities" }}
        </el-button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <el-skeleton :rows="5" animated />
    </div>

    <!-- Main content -->
    <div v-else-if="entity" class="detail-content">
      <!-- Entity Header Card -->
      <el-card class="entity-header-card">
        <div class="entity-header">
          <div class="entity-icon">
            <el-icon :size="48"><User /></el-icon>
          </div>
          <div class="entity-info">
            <div v-if="!isEditing" class="entity-name-row">
              <h1>{{ entity.name }}</h1>
              <!-- <el-button :icon="Edit" text circle @click="startEditing" /> -->
            </div>
            <div v-else class="entity-name-edit">
              <el-input
                v-model="editedName"
                size="large"
                placeholder="Entity name"
                @keyup.enter="saveEntityName"
                @keyup.escape="cancelEditing"
              />
              <el-button type="primary" @click="saveEntityName">Save</el-button>
              <el-button @click="cancelEditing">Cancel</el-button>
            </div>
            <div class="entity-fields">
              <div
                v-for="field in entityFields"
                :key="`${field.label}-${field.value}`"
                class="meta-field"
              >
                <el-icon v-if="field.label.toLowerCase().includes('email')">
                  <Message />
                </el-icon>
                <el-icon
                  v-else-if="field.label.toLowerCase().includes('phone')"
                >
                  <Phone />
                </el-icon>
                <el-icon v-else-if="field.label.toLowerCase().includes('role')">
                  <Briefcase />
                </el-icon>
                <el-icon
                  v-else-if="
                    field.label.toLowerCase().includes('primary contact')
                  "
                >
                  <User />
                </el-icon>
                <el-icon
                  v-else-if="field.label.toLowerCase().includes('notes')"
                >
                  <Document />
                </el-icon>
                <strong>{{ field.label }}:</strong>
                <span>{{ field.value }}</span>
              </div>
            </div>
          </div>
          <div class="entity-actions">
            <el-button
              type="primary"
              :icon="Edit"
              @click="showEntityDetailsForm = !showEntityDetailsForm"
            >
              {{ showEntityDetailsForm ? "Hide Details" : "Edit Details" }}
            </el-button>
            <!-- <el-dropdown trigger="click" @command="createWorkflow">
              <el-button type="primary" :icon="Plus">
                New Workflow
                <el-icon class="el-icon--right">
                  <i class="el-icon-arrow-down" />
                </el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="wfDef in workflowDefinitions"
                    :key="wfDef.guid"
                    :command="wfDef.guid"
                  >
                    <el-icon><Document /></el-icon>
                    {{ wfDef.name }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="workflowDefinitions.length === 0"
                    disabled
                  >
                    No workflow types available
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown> -->
            <el-button :icon="Refresh" @click="loadData">Refresh</el-button>
          </div>
        </div>
      </el-card>

      <!-- Entity Details Form (inline) -->
      <el-card v-if="showEntityDetailsForm" class="entity-details-card">
        <template #header>
          <div class="card-header">
            <span>Entity Details</span>
          </div>
        </template>
        <FormResponseTemplate
          v-if="entityContent && entity"
          :form-guid="entity.formResponse.formDefinitionGuid"
          :response-guid="entityContent.formResponse.guid"
          :entity="entity"
        />
      </el-card>

      <!-- Quick Stats -->
      <!-- <div class="stats-row">
        <el-card class="stat-card">
          <div class="stat-value active">{{ stats.active }}</div>
          <div class="stat-label">Active</div>
        </el-card>
        <el-card class="stat-card">
          <div class="stat-value pending">{{ stats.pending }}</div>
          <div class="stat-label">Pending</div>
        </el-card>
        <el-card class="stat-card">
          <div class="stat-value completed">{{ stats.completed }}</div>
          <div class="stat-label">Completed</div>
        </el-card>
        <el-card class="stat-card">
          <div class="stat-value">{{ stats.lastContact }}</div>
          <div class="stat-label">Last Activity</div>
        </el-card>
      </div> -->

      <EntityWorkflowsPanel
        v-if="entityGuid"
        :entity-guid="entityGuid"
        :include-archived="false"
        :show-interactions="true"
        @workflow-selected="handleWorkflowSelected"
      />
    </div>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <el-empty description="Entity not found">
        <el-button type="primary" @click="goBack">Go Back</el-button>
      </el-empty>
    </div>

    <!-- Workflow Detail Drawer -->
    <!-- <el-drawer
      v-model="workflowDrawerOpen"
      direction="rtl"
      :size="drawerWidth"
      :destroy-on-close="true"
      @close="handleDrawerClose"
    >
      <template #header>
        <h3>{{ selectedWorkflow?.workflowName ?? "Workflow Details" }}</h3>
      </template> -->

    <!-- Housing Application Viewer -->
    <HousingApplicationEntityViewer
      v-if="selectedHousingApp"
      :housing-app="selectedHousingApp"
      :drawer-open="housingAppDrawerOpen"
      :initial-tab="ApplicationDrawerTabs.CONTACTHISTORY"
      @drawer-closed="handleHousingAppDrawerClosed"
    />

    <WorkflowResponseViewer
      v-if="selectedWorkflow"
      :compliance-workflow="selectedWorkflow"
      :full-screen="false"
      @close-drawer="handleDrawerClose"
      @update-workflow="handleWorkflowUpdated"
      @refresh-list="handleWorkflowUpdated"
    />
    <!-- </el-drawer> -->
  </div>
</template>

<style lang="less" scoped>
.CRMEntityDetail {
  height: 100%;
  overflow-y: auto;

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .loading-state {
    padding: 2rem;
  }

  .detail-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .entity-header-card {
    .entity-header {
      display: flex;
      align-items: flex-start;
      gap: 1.5rem;

      .entity-icon {
        flex-shrink: 0;
        width: 80px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--el-fill-color-light);
        border-radius: 50%;
        color: var(--el-color-primary);
      }

      .entity-info {
        flex: 1;

        .entity-name-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          h1 {
            margin: 0;
            font-size: 1.75rem;
            font-weight: 600;
          }
        }

        .entity-name-edit {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;

          .el-input {
            max-width: 400px;
          }
        }

        .entity-fields {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-top: 0.5rem;

          .meta-field {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            font-size: 0.9rem;
            color: var(--el-text-color-secondary);

            .el-icon {
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 16px;
              margin-right: 0.25rem;
            }
          }
        }
      }

      .entity-actions {
        display: flex;
        gap: 0.5rem;
        flex-shrink: 0;
      }
    }
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;

    .stat-card {
      text-align: center;

      :deep(.el-card__body) {
        padding: 1rem;
      }

      .stat-value {
        font-size: 1.75rem;
        font-weight: 600;
        margin-bottom: 0.25rem;

        &.active {
          color: var(--el-color-success);
        }

        &.pending {
          color: var(--el-color-warning);
        }

        &.completed {
          color: var(--el-color-info);
        }
      }

      .stat-label {
        font-size: 0.85rem;
        color: var(--el-text-color-secondary);
      }
    }
  }

  .entity-details-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 600;
    }
  }

  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
  }
}
</style>
